/**
 * "Míralo en acción" (snippets/product-look-module.liquid).
 *
 * <look-carousel> — the horizontal card row: prev/next arrows (shown only
 * when the row overflows and the device has a pointer), keyboard scrolling,
 * and pausing a playing video once it scrolls out of view.
 *
 * <product-video> — one VIDEO card. It ships as a poster + play button and a
 * <template> holding the real <video>; nothing is downloaded until the
 * visitor presses play. Then the player is mounted in place (native controls
 * for pause / seek / fullscreen), and any other playing card is paused first,
 * so only one plays at a time. Image cards are plain markup with no JS.
 */
(function initProductLook() {
  const playing = new Set();

  if (!customElements.get('product-video')) {
    customElements.define(
      'product-video',
      class ProductVideo extends HTMLElement {
        connectedCallback() {
          this.playButton = this.querySelector('[data-pdp-video-play]');
          if (!this.playButton) return;
          this.playButton.addEventListener('click', () => this.play());
        }

        mount() {
          if (this.video) return this.video;
          const template = this.querySelector('template[data-pdp-video-template]');
          if (!template) return null;
          this.appendChild(template.content.cloneNode(true));
          this.video = this.querySelector('video');
          this.video?.addEventListener('ended', () => this.pause());
          this.video?.addEventListener('pause', () => playing.delete(this));
          this.video?.addEventListener('play', () => playing.add(this));
          return this.video;
        }

        play() {
          playing.forEach((other) => {
            if (other !== this) other.pause();
          });
          const video = this.mount();
          if (!video) return;
          this.classList.add('pdp-look__item--playing');
          video.focus({ preventScroll: true });
          video.play().catch(() => {
            /* blocked by the browser: the native controls are visible, the visitor can press play */
          });
          playing.add(this);
        }

        pause() {
          if (this.video && !this.video.paused) this.video.pause();
          playing.delete(this);
        }
      }
    );
  }

  if (!customElements.get('look-carousel')) {
    customElements.define(
      'look-carousel',
      class LookCarousel extends HTMLElement {
        connectedCallback() {
          this.viewport = this.querySelector('[data-look-viewport]');
          this.prev = this.querySelector('[data-look-prev]');
          this.next = this.querySelector('[data-look-next]');
          if (!this.viewport) return;

          this.prev?.addEventListener('click', () => this.scrollByCard(-1));
          this.next?.addEventListener('click', () => this.scrollByCard(1));
          this.viewport.addEventListener('scroll', () => this.update(), { passive: true });
          this.viewport.addEventListener('keydown', (event) => {
            if (event.target !== this.viewport) return;
            if (event.key === 'ArrowRight') {
              event.preventDefault();
              this.scrollByCard(1);
            } else if (event.key === 'ArrowLeft') {
              event.preventDefault();
              this.scrollByCard(-1);
            }
          });

          if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(() => this.update());
            this.resizeObserver.observe(this.viewport);
          }

          // Pause a playing video that scrolled mostly out of view.
          if ('IntersectionObserver' in window) {
            this.visibility = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  if (!entry.isIntersecting) entry.target.closest('product-video')?.pause();
                });
              },
              { root: this.viewport, threshold: 0.4 }
            );
            this.querySelectorAll('product-video').forEach((card) => this.visibility.observe(card));
          }

          this.update();
        }

        disconnectedCallback() {
          this.resizeObserver?.disconnect();
          this.visibility?.disconnect();
        }

        get behavior() {
          return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        }

        scrollByCard(direction) {
          const slide = this.viewport.querySelector('.pdp-look__slide');
          const step = slide ? slide.getBoundingClientRect().width + 8 : this.viewport.clientWidth * 0.8;
          this.viewport.scrollBy({ left: direction * step, behavior: this.behavior });
        }

        update() {
          const { scrollLeft, scrollWidth, clientWidth } = this.viewport;
          const overflows = scrollWidth > clientWidth + 1;
          [this.prev, this.next].forEach((button) => {
            if (button) button.hidden = !overflows;
          });
          if (this.prev) this.prev.disabled = scrollLeft <= 1;
          if (this.next) this.next.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
        }
      }
    );
  }
})();
